import { useEffect, useState } from "react";
import axios, { API } from "@/lib/api";
import { formatApiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Users2, Lock, Loader2, Share2 } from "lucide-react";

/**
 * SharePortfolioDialog — lets the owner share a portfolio into one of
 * their workspaces (team / enterprise). On confirm, hits
 * POST /api/studio/portfolios/{id}/share with `{workspace_id}`.
 *
 * If the user has no workspaces, shows a friendly nudge to create one
 * (link to /enterprise/team).
 */
export default function SharePortfolioDialog({ portfolio, open, onOpenChange, onChanged }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Local selection — defaults to portfolio's current shared workspace if any.
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open || !portfolio) return;
    setLoading(true);
    setSelected(portfolio.shared_workspace_id || null);
    axios.get(`${API}/workspaces/me`)
      .then((r) => {
        // Defense-in-depth — only `active` memberships can act as a
        // sharing target. Backend filters to active∪invited, but a
        // pending invitee can't actually be granted read access until
        // they accept, so hide them in the picker.
        const list = (r.data?.workspaces || []).filter(
          (w) => !w.my_role || w.my_role === "lead" || w.my_role === "member",
        );
        setWorkspaces(list);
      })
      .catch(() => toast.error("Could not load your workspaces."))
      .finally(() => setLoading(false));
  }, [open, portfolio]);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/studio/portfolios/${portfolio.id}/share`,
        { workspace_id: selected },
      );
      toast.success(
        selected
          ? "Portfolio shared with team."
          : "Portfolio is now private.",
      );
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't update sharing."));
    } finally {
      setSaving(false);
    }
  };

  if (!portfolio) return null;

  const dirty = (portfolio.shared_workspace_id || null) !== (selected || null);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent
        data-testid="portfolio-share-dialog"
        className="bg-[#0E0E0E] border border-white/10 text-white p-0 rounded-2xl w-[94vw] max-w-[440px] sm:!rounded-2xl"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/8 space-y-1">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
            <Share2 className="w-3 h-3" /> Share portfolio
          </div>
          <DialogTitle className="font-display text-xl tracking-tight text-white">
            {portfolio.name}
          </DialogTitle>
          <DialogDescription className="text-white/50 text-[11px] leading-snug">
            Team members will be able to view and export this portfolio.
            They can&apos;t add, remove, rename or delete pieces.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 space-y-3">
          {loading ? (
            <div className="py-8 text-center text-sm text-white/55 inline-flex items-center justify-center gap-2 w-full">
              <Loader2 className="w-4 h-4 animate-spin text-[#D4AF37]" /> Loading workspaces…
            </div>
          ) : workspaces.length === 0 ? (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-[12px] text-white/75">
              You&apos;re not part of any team yet. <a href="/enterprise/team" className="text-[#D4AF37] underline-offset-2 hover:underline">Spin up a team</a> first, invite your designers, then come back to share this portfolio.
            </div>
          ) : (
            <>
              {/* Make-private radio */}
              <label
                className={`flex items-center gap-3 rounded-xl border px-3.5 py-3 cursor-pointer transition ${
                  selected === null
                    ? "border-[#D4AF37]/60 bg-[#D4AF37]/8"
                    : "border-white/10 hover:border-white/25"
                }`}
                data-testid="portfolio-share-private"
              >
                <input
                  type="radio"
                  name="share-target"
                  className="accent-[#D4AF37]"
                  checked={selected === null}
                  onChange={() => setSelected(null)}
                />
                <Lock className="w-3.5 h-3.5 text-white/55" />
                <div>
                  <div className="text-sm text-white">Private</div>
                  <div className="text-[11px] text-white/45">Only you can see this portfolio.</div>
                </div>
              </label>

              {workspaces.map((ws) => (
                <label
                  key={ws.id}
                  className={`flex items-center gap-3 rounded-xl border px-3.5 py-3 cursor-pointer transition ${
                    selected === ws.id
                      ? "border-[#D4AF37]/60 bg-[#D4AF37]/8"
                      : "border-white/10 hover:border-white/25"
                  }`}
                  data-testid={`portfolio-share-ws-${ws.id}`}
                >
                  <input
                    type="radio"
                    name="share-target"
                    className="accent-[#D4AF37]"
                    checked={selected === ws.id}
                    onChange={() => setSelected(ws.id)}
                  />
                  <Users2 className="w-3.5 h-3.5 text-[#D4AF37]" />
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{ws.name}</div>
                    <div className="text-[11px] text-white/45">
                      {ws.is_lead ? "Lead" : ws.my_role || "Member"}
                      {ws.plan_name ? ` · ${ws.plan_name}` : ""}
                    </div>
                  </div>
                </label>
              ))}
            </>
          )}

          <div className="pt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="text-xs px-4 py-2 rounded-full border border-white/15 text-white/75 hover:bg-white/5 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving || !dirty || loading}
              data-testid="portfolio-share-submit"
              className="text-xs px-4 py-2 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              {selected === null ? "Make private" : "Share"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
